import React, { useEffect, useState, useReducer, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import MaterialTable from "material-table";
import Typography from "@material-ui/core/Typography";
import { Paper, Chip, Grid, Button } from "@material-ui/core";
import { blue, green, grey, red } from "@material-ui/core/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faFilter,
  faSearch,
  faSortUp,
  faTimes,
  faUsersCog,
  faList,
} from "@fortawesome/free-solid-svg-icons";

import { MaterialTableLocaleDelayed } from "../../../i18n/material-table";
import { _t } from "../../../i18n/lang";
import AjaxLoading from "../../AjaxLoading";

import { modernReportingService } from "../../../services/modernReportingService";
import { _url } from "../../../helpers/makeUrl";
import StaticPage from "../../StaticPage";
import { StaticPageType } from "../../MSSurfaceZone/types";

const tableIcons = {
  NextPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleRight} {...props} />
  )),
  PreviousPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleLeft} {...props} />
  )),
  FirstPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleDoubleLeft} {...props} />
  )),
  LastPage: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faAngleDoubleRight} {...props} />
  )),
  Search: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faSearch} {...props} />
  )),
  SortArrow: React.forwardRef((props, ref) => (
    <FontAwesomeIcon {...props} icon={faSortUp} />
  )),
  ResetSearch: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faTimes} {...props} size="xs" />
  )),
  Filter: React.forwardRef((props, ref) => (
    <FontAwesomeIcon icon={faFilter} {...props} />
  )),
};

const useStyles = makeStyles((theme) => ({
  wrap: {
    flexGrow: 1,
    paddingRight: "1em",
    paddingLeft: "1em",
  },
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  tableHeader: {
    color: "rgba(0, 0, 0, .54)",
    marginBottom: "1em",
  },
  headerIcon: {
    marginRight: "8px",
  },
  iconColor: {
    color: "#fff",
  },
  fabStyle: {
    marginRight: "1em",
    backgroundColor: "#cb0101",
  },
  headerColor: {
    color: "#cb0101",
  },
  headerWrap: {
    borderBottom: "1px solid #ccc",
    paddingBottom: "16px",
    marginBottom: "20px",
  },
}));

const statusColor = (status) => {
  if (parseInt(status, 10) === 0) {
    return red[500];
  }
  if (parseInt(status, 10) === 1) {
    return green[500];
  }
  return "blueGrey";
};

export default function CurrentOrders() {
  const [currentOrders, setCurrentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  let history = useHistory();

  useEffect(() => {
    async function fetchData() {
      const { orders } = await modernReportingService.currentOrders();
      setCurrentOrders(orders);
      setLoading(false);
    }

    fetchData();
  }, []);

  const handleAssignCustomersToOrder = (orderId) => {
    history.push(_url(`/current-order/${orderId}`));
  };

  const classes = useStyles();

  const styleCss = {
    fontSize: "10px",
  };

  const columns = [
    {
      title: _t("MMR_CURRENT_ORDER_TABLE_HEAD_ID"),
      field: "soNbr",
      cellStyle: { textAlign: "center" },
      headerStyle: { textAlign: "center" },
    },
    {
      title: _t("MMR_CURRENT_ORDER_TABLE_HEAD_STATUS"),
      field: "customerFilledStatus",
      cellStyle: { textAlign: "center" },
      headerStyle: {
        textAlign: "center",
      },
      render(rowData) {
        return (
          <Chip
            style={{
              backgroundColor: statusColor(rowData.customerFilledStatus),
            }}
            label={_t(`MMR_STATUS_${rowData.customerFilledStatus}`)}
            size="small"
          />
        );
      },
    },
    {
      title: _t("MMR_CURRENT_ORDER_TABLE_HEAD_ORDER_DATE"),
      field: "orderDate",
    },
    {
      title: _t("MMR_CURRENT_ORDER_TABLE_HEAD_CONF_DATE"),
      field: "confirmationDate",
    },
    {
      title: _t("MMR_CURRENT_ORDER_TABLE_HEAD_MFG_ID"),
      field: "partnerMfgId",
      render(rowData) {
        return <label style={styleCss}>{rowData.partnerMfgId}</label>;
      },
    },
    {
      title: _t("MMR_CURRENT_ORDER_TABLE_HEAD_Partner"),
      field: "partnerName",
      render(rowData) {
        return <label style={styleCss}>{rowData.partnerName}</label>;
      },
    },
  ];

  return (
    <div className={classes.wrap}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <StaticPage id={StaticPageType.warranties} />
            {loading ? (
              <div style={{ position: "relative", minHeight: "10em" }}>
                <AjaxLoading />
              </div>
            ) : (
              <div style={{ padding: "2em 0em 5em 5em" }}>
                <MaterialTable
                  columns={columns}
                  data={currentOrders}
                  title={_t("MMR_ASSIGNN_CUSTOMER_TO_ORDERS")}
                  localization={MaterialTableLocaleDelayed()}
                  options={{
                    showTitle: true,
                    paging: false,
                    actionsColumnIndex: -1,
                    detailPanelColumnAlignment: "right",
                    pageSizeOptions: [5, 10, 20, 50, 100],
                    headerStyle: {
                      backgroundColor: grey[300],
                    },
                  }}
                  actions={[
                    (rowData) => {
                      let tooltip = _t("MMR_ASSIGN_CUSTOMERS");
                      return {
                        icon() {
                          return (
                            <>
                              <FontAwesomeIcon
                                style={{ color: blue[500] }}
                                icon={faUsersCog}
                                size="xs"
                              />
                            </>
                          );
                        },
                        onClick: (event, rowData) => {
                          handleAssignCustomersToOrder(rowData.soNbr);
                        },
                        tooltip,
                      };
                    },
                  ]}
                  icons={tableIcons}
                  components={{
                    Toolbar() {
                      return (
                        <Typography
                          variant="h4"
                          gutterBottom
                          className={classes.headerWrap}
                        >
                          <FontAwesomeIcon
                            icon={faList}
                            className={classes.headerIcon}
                          />
                          {_t("MMR_CURRENT_ORDERS_HEADERS")}
                        </Typography>
                      );
                    },
                  }}
                />
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

